import { z } from 'zod';

export const metricCategorySchema = z.enum(['business', 'quality', 'risk', 'ai']);

export type MetricCategory = z.infer<typeof metricCategorySchema>;

const UNKNOWN_BASELINE_FR = 'À mesurer';
const UNKNOWN_TARGET_FR = 'À confirmer';
const UNKNOWN_OWNER_FR = 'À confirmer';
const UNKNOWN_SOURCE_FR = 'À confirmer';
const UNKNOWN_BASELINE_EN = 'To measure';
const UNKNOWN_TARGET_EN = 'To confirm';
const UNKNOWN_OWNER_EN = 'To confirm';
const UNKNOWN_SOURCE_EN = 'To confirm';

function unknownOrTrimmed(value: string | undefined | null, fallback: string) {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : fallback;
}

export function createMetricSchema(lang: 'fr' | 'en') {
  const fallback = {
    baseline: lang === 'en' ? UNKNOWN_BASELINE_EN : UNKNOWN_BASELINE_FR,
    target: lang === 'en' ? UNKNOWN_TARGET_EN : UNKNOWN_TARGET_FR,
    owner: lang === 'en' ? UNKNOWN_OWNER_EN : UNKNOWN_OWNER_FR,
    dataSource: lang === 'en' ? UNKNOWN_SOURCE_EN : UNKNOWN_SOURCE_FR,
  };

  return z
    .object({
      name: z.string().min(1),
      category: metricCategorySchema,
      reason: z.string().min(1),
      formula: z.string().min(1),
      unit: z.string().min(1),
      baseline: z.string().default(fallback.baseline),
      target: z.string().default(fallback.target),
      dataSource: z.string().default(fallback.dataSource),
      cadence: z.string().min(1),
      owner: z.string().default(fallback.owner),
      decisionThreshold: z.string().min(1),
      pairedGuardrail: z.string().min(1),
    })
    .transform((metric) => ({
      ...metric,
      baseline: unknownOrTrimmed(metric.baseline, fallback.baseline),
      target: unknownOrTrimmed(metric.target, fallback.target),
      owner: unknownOrTrimmed(metric.owner, fallback.owner),
      dataSource: unknownOrTrimmed(metric.dataSource, fallback.dataSource),
    }));
}

export type Metric = z.infer<ReturnType<typeof createMetricSchema>>;

export const debtItemSchema = z.object({
  type: z.string().min(1),
  description: z.string().min(1),
  impact: z.string().min(1),
  mitigation: z.string().min(1),
});

export type DebtItem = z.infer<typeof debtItemSchema>;

export const aiEvaluationSchema = z.object({
  isAiRelevant: z.boolean(),
  measures: z.array(z.string().min(1)),
  riskSummary: z.string().min(1),
});

export type AiEvaluation = z.infer<typeof aiEvaluationSchema>;

export const decisionRuleSchema = z.object({
  condition: z.string().min(1),
  action: z.string().min(1),
});

export type DecisionRule = z.infer<typeof decisionRuleSchema>;

export function createMeasurementPlanSchema(lang: 'fr' | 'en') {
  return z.object({
    title: z.string().min(1),
    executiveSummary: z.string().min(1),
    businessObjective: z.string().min(1),
    northStarMetric: z.string().min(1),
    baseline: z.string().min(1),
    assumptions: z.array(z.string().min(1)).min(1),
    scope: z.array(z.string().min(1)).min(1),
    nonScope: z.array(z.string().min(1)).min(1),
    metrics: z.array(createMetricSchema(lang)).min(1).max(8),
    instrumentationPlan: z.array(z.string().min(1)).min(1),
    acceptanceCriteria: z.array(z.string().min(1)).min(1),
    technicalDebt: z.array(debtItemSchema).min(1),
    businessDebt: z.array(debtItemSchema).min(1),
    aiEvaluation: aiEvaluationSchema,
    continueCorrectPivotStopRules: z.array(decisionRuleSchema).min(1),
    handoverCriteria: z.array(z.string().min(1)).min(1),
    missingInformation: z.array(z.string().min(1)).min(1),
  });
}

export type MeasurementPlan = z.infer<ReturnType<typeof createMeasurementPlanSchema>>;
